import axios from 'axios';

// Relative API path — sabai request same origin (:3001) bata janchha ra
// CRA proxy (package.json "proxy") le localhost:5001 ma forward garchha.
// Yesle garera mobile/LAN device ma EUTAI PORT (3001) chahiye — 5001 kholnu pardaina.
const API_URL =
  process.env.REACT_APP_API_URL ||
  '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Absolute base — media zip download ko lagi (proxy bata janchha)
export const API_BASE = process.env.REACT_APP_API_URL
  ? API_URL.replace(/\/api$/, '')
  : window.location.origin;

// ============================================================
// Local cache — news fast loading (pahilo paint cache bata) +
// offline fallback. stale-while-revalidate pattern.
// ============================================================
export const getCached = (key) => {
  try {
    const raw = localStorage.getItem(`kalo_cache_${key}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const setCached = (key, data) => {
  try {
    localStorage.setItem(`kalo_cache_${key}`, JSON.stringify({ t: Date.now(), data }));
  } catch {
    // storage quota — purano cache hataera feri try
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith('kalo_cache_'))
        .forEach((k) => localStorage.removeItem(k));
      localStorage.setItem(`kalo_cache_${key}`, JSON.stringify({ t: Date.now(), data }));
    } catch { /* ignore */ }
  }
};

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Guard: SPA fallback ya proxy error ko HTML/plain-text body aayeko sadhai string hunchha.
// Array chahine thau ma string aayepachhi 'o.map is not a function' crash chaldechha.
// Yesle kunai pani page blank/empty huna na deuna — string body lai [] (khali array) ma bhaya.
// NOTE: Online kaliyo backend le data JSON (object/array) pharkauchha — string body = error matra.
api.interceptors.response.use(
  (response) => {
    if (typeof response.data === 'string') {
      response.data = []; // error/fallback page — khali result, crash bhayena
    }
    return response;
  },
  (error) => Promise.reject(error)
);

// Pass FormData through as-is (Object.keys() on FormData returns []), otherwise build it
const buildFormData = (data) => {
  if (data instanceof FormData) return data;
  const formData = new FormData();
  Object.keys(data || {}).forEach((key) => {
    if (key === 'photos' && Array.isArray(data[key])) {
      data[key].forEach((file) => formData.append('photos', file));
    } else if (key === 'image' || key === 'video' || key === 'authorImage') {
      if (data[key]) formData.append(key, data[key]);
    } else {
      formData.append(key, data[key]);
    }
  });
  return formData;
};

// News API
export const newsApi = {
  getAll: (params) => api.get('/news', { params }),
  getOne: (id) => api.get(`/news/${id}`),
  getTrending: (limit = 10) => api.get('/news/trending', { params: { limit } }),
  getByAuthor: (author, params = {}) => api.get('/news', { params: { author, ...params } }),
  create: (data) =>
    api.post('/news', buildFormData(data), {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  update: (id, data) =>
    api.put(`/news/${id}`, buildFormData(data), {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  delete: (id) => api.delete(`/news/${id}`),
};

// Categories API
export const categoryApi = {
  getAll: () => api.get('/categories'),
  getOne: (id) => api.get(`/categories/${id}`),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
};

// Reels API
export const reelApi = {
  getAll: () => api.get('/reels'),
  getOne: (id) => api.get(`/reels/${id}`),
  create: (data) =>
    api.post('/reels', buildFormData(data), {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  update: (id, data) =>
    api.put(`/reels/${id}`, buildFormData(data), {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  delete: (id) => api.delete(`/reels/${id}`),
};

// Ads API
export const adApi = {
  getAll: (active) => api.get('/ads', { params: { active } }),
  getByPosition: (position) => api.get(`/ads/position/${position}`),
  getOne: (id) => api.get(`/ads/${id}`),
  create: (data) =>
    api.post('/ads', buildFormData(data), {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  update: (id, data) =>
    api.put(`/ads/${id}`, buildFormData(data), {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  delete: (id) => api.delete(`/ads/${id}`),
  recordClick: (id) => api.post(`/ads/${id}/click`),
};

// Footer / site settings API
export const footerApi = {
  getSettings: () => api.get('/footer'),
  updateSettings: (data, logoFiles) => {
    const fd = new FormData();
    Object.entries(data || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null) fd.append(k, typeof v === 'object' ? JSON.stringify(v) : String(v));
    });
    if (logoFiles && typeof logoFiles === 'object') {
      Object.entries(logoFiles).forEach(([field, file]) => {
        // Array bhaye eutai field name ma dherai file append (jastai teamPhotos)
        if (Array.isArray(file)) file.forEach((f) => f && fd.append(field, f));
        else if (file) fd.append(field, file);
      });
    }
    return api.put('/footer', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Contact messages API
export const contactApi = {
  create: (data) => api.post('/contacts', data),
  getAll: () => api.get('/contacts'),
  markRead: (id) => api.put(`/contacts/${id}/read`),
  remove: (id) => api.delete(`/contacts/${id}`),
};

// Subscribe API
export const subscribeApi = {
  subscribe: (email) => api.post('/subscribe', { email }),
  unsubscribe: (email) => api.delete('/subscribe', { params: { email } }),
  delete: (email) => api.delete('/subscribe', { params: { email } }),
  getAll: () => api.get('/subscribe'),
  toggleActive: (email) => api.put(`/subscribe/${email}/toggle`),
};

// Comments API
export const commentApi = {
  getByNews: (newsId) => api.get(`/comments/news/${newsId}`),
  getAll: () => api.get('/comments'),
  create: (data) => api.post('/comments', data),
  update: (id, data) => api.put(`/comments/${id}`, data),
  reply: (id, adminReply) => api.put(`/comments/${id}/reply`, { admin_reply: adminReply }),
  delete: (id) => api.delete(`/comments/${id}`),
};

// Notice modal API
export const noticeApi = {
  getActive: () => api.get('/notice/active'),
  getAll: () => api.get('/notice'),
  create: (data, file) => {
    const fd = new FormData();
    Object.entries(data || {}).forEach(([k, v]) => v !== undefined && v !== null && fd.append(k, String(v)));
    if (file) fd.append('noticeImage', file);
    return api.post('/notice', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  update: (id, data, file) => {
    const fd = new FormData();
    Object.entries(data || {}).forEach(([k, v]) => v !== undefined && v !== null && fd.append(k, String(v)));
    if (file) fd.append('noticeImage', file);
    return api.put(`/notice/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  remove: (id) => api.delete(`/notice/${id}`),
};

// Auth API
export const authApi = {
  // STEP 1 — popup modal gate password
  verifyModal: (password) => api.post('/auth/verify-modal', { password }),
  // STEP 2 — admin account login (इमेल + पासवर्ड दुवै)
  login: (email, password) => api.post('/auth/login', { email, password }),
  me: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) =>
    api.put('/auth/change-password', { currentPassword, newPassword }),
  changeModalPassword: (currentPassword, newPassword) =>
    api.put('/auth/modal-password', { currentPassword, newPassword }),
};

// Admin credentials management API
export const adminApi = {
  getAll: () => api.get('/auth/admins'),
  create: (data) => api.post('/auth/admins', data),
  remove: (id) => api.delete(`/auth/admins/${id}`),
};

// Media (uploads folder) API
export const mediaApi = {
  getAll: () => api.get('/media'),
  deletePhoto: (category, filename) => api.delete(`/media/${category}/${encodeURIComponent(filename)}`),
  downloadZipUrl: (category) => `${API_BASE}/api/media/download-zip${category ? `?category=${encodeURIComponent(category)}` : ''}`,
};

// Site views / analytics API
export const statsApi = {
  recordView: () => api.post('/stats/view'),
  getViews: () => api.get('/stats/views'),
};

// Weather API
export const weatherApi = {
  getWeather: (city) => api.get('/weather', { params: city ? { city } : {} }),
};

export default api;